import { Rectangle } from "./Rectangle";
import { Circle } from "./Circle";
import { Distance } from "./Distance";



export class LineToLineIntersect {
    x: number;
    y: number;
    onLine1: boolean;
    onLine2: boolean;
}



export class Intersects {
    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    static LineToLine(x11: number, y11: number, x12: number, y12: number, x21: number, y21: number, x22: number, y22: number, res: pc.Vec2): boolean {
        // Check if none of the lines are of length 0
        if ((x11 === x12 && y11 === y12) || (x21 === x22 && y21 === y22))
            return false;

        let denominator = ((y22 - y21) * (x12 - x11) - (x22 - x21) * (y12 - y11))

        // Lines are parallel
        if (denominator === 0)
            return false;

        let ua = ((x22 - x21) * (y11 - y21) - (y22 - y21) * (x11 - x21)) / denominator
        let ub = ((x12 - x11) * (y11 - y21) - (y12 - y11) * (x11 - x21)) / denominator

        // is the intersection along the segments
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1)
            return false;

        // Return a object with the x and y coordinates of the intersection
        res.x = x11 + ua * (x12 - x11);
        res.y = y11 + ua * (y12 - y11);

        return true;
    }

    //-------------------------------------------------------------------------
    /**
     * 
     * @param x11 
     * @param y11 
     * @param x12 
     * @param y12 
     * @param x21 
     * @param y21 
     * @param x22 
     * @param y22 
     * @param res 
     * @returns If the lines intersect, the result contains the x and y of the intersection (treating the lines as infinite) and booleans for whether line segment 1 or line segment 2 contain the point
     */
    //-------------------------------------------------------------------------
    static LineToLineInfinite(x11: number, y11: number, x12: number, y12: number, x21: number, y21: number, x22: number, y22: number, res: LineToLineIntersect): boolean {
        let denominator = ((y22 - y21) * (x12 - x11)) - ((x22 - x21) * (y12 - y11));

        if (denominator == 0)
            return false;

        let a = y11 - y21;
        let b = x11 - x21;
        let numerator1 = ((x22 - x21) * a) - ((y22 - y21) * b);
        let numerator2 = ((x12 - x11) * a) - ((y12 - y11) * b);

        a = numerator1 / denominator;
        b = numerator2 / denominator;

        // If we cast these lines infinitely in both directions, they intersect here:
        res.x = x11 + (a * (x12 - x11));
        res.y = y11 + (a * (y12 - y11));

        // If line1 is a segment and line2 is infinite, they intersect if:
        res.onLine1 = (a > 0 && a < 1);

        // If line2 is a segment and line1 is infinite, they intersect if:
        res.onLine2 = (b > 0 && b < 1);

        // if line1 and line2 are segments, they intersect if both of the above are true
        return true;
    }

    //-------------------------------------------------------------------------
    /**
     * Checks if two Rectangles intersect.
     * A Rectangle intersects another Rectangle if any part of its bounds is within the other Rectangle's bounds. As such, the two Rectangles are considered "solid". A Rectangle with no width or no height will never intersect another Rectangle.
     * @param rectA 
     * @param rectB 
     * @returns `true` if the two Rectangles intersect, otherwise `false`.
     */
    //-------------------------------------------------------------------------
    static RectangleToRectangle(rectA: Rectangle, rectB: Rectangle): boolean {
        if (rectA.width <= 0 || rectA.height <= 0 || rectB.width <= 0 || rectB.height <= 0) {
            return false;
        }

        return !(rectA.right < rectB.x || rectA.bottom < rectB.y || rectA.x > rectB.right || rectA.y > rectB.bottom);
    }

    //-------------------------------------------------------------------------
    /**
     * Checks for intersection between a circle and a rectangle.
     * @param circle 
     * @param rect
     * @returns `true` if the two objects intersect, otherwise `false`.
     */
    //-------------------------------------------------------------------------
    static CircleToRectable(circle: Circle, rect: Rectangle): boolean {
        var halfWidth = rect.width / 2;
        var halfHeight = rect.height / 2;

        var cx = Math.abs(circle.x - rect.x - halfWidth);
        var cy = Math.abs(circle.y - rect.y - halfHeight);
        var xDist = halfWidth + circle.radius;
        var yDist = halfHeight + circle.radius;

        if (cx > xDist || cy > yDist) {
            return false;
        }
        else if (cx <= halfWidth || cy <= halfHeight) {
            return true;
        }
        else {
            var xCornerDist = cx - halfWidth;
            var yCornerDist = cy - halfHeight;
            var xCornerDistSq = xCornerDist * xCornerDist;
            var yCornerDistSq = yCornerDist * yCornerDist;
            var maxCornerDistSq = circle.radius * circle.radius;

            return (xCornerDistSq + yCornerDistSq <= maxCornerDistSq);
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Checks if two Circles intersect.
     * @param circleA 
     * @param circleB 
     */
    //-------------------------------------------------------------------------
    static CircleToCircle(circleA: Circle, circleB: Circle): boolean {
        return (Distance.Between(circleA.x, circleA.y, circleB.x, circleB.y) <= (circleA.radius + circleB.radius));
    }
}